import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, map, Observable, of, switchMap, take } from "rxjs";

import { OfferActivationWizardComponent } from "../offer-activation-wizard/offer-activation-wizard.component";
import { Organization } from "../../organizations/models/organizations.entity";
import { OrganizationRepository } from "../../organizations/repositories/organization.repository";
import { OrganizationStoreService } from "../../organizations/services/organization.store.service";
import { DialogService } from "../../shared/components/dialog/dialog.service";
import { StorageService } from "../../shared/storage/storage.service";
import { getOfferContextSnapshot } from "../../organizations/utils/plan-offer-marketing";
import { OfferPlanCode } from "../../shared/types/offer-upsell";
import { Role } from "../../users/models/users.entity";
import { Talker } from "../../users/models/talker.entity";
import { Profile, ProfileService } from "../../users/services/profile.service";
import { ManagedOfferCheckoutService } from "../../organizations/services/managed-offer-checkout.service";

type OfferPricingCard = {
  planCode: OfferPlanCode;
  tierLabel: string;
  priceLabel: string;
  metricValue: string;
  metricSuffix?: string;
  metricNote?: string;
  bullets: string[];
  boldBulletCount?: number;
  includedLabel?: string;
  includedValue?: string;
  promoLabel?: string;
  promoOriginalPrice?: string;
  promoPrice?: string;
  promoBadge?: string;
  ctaLabel: string;
  featured?: boolean;
};

@Component({
  selector: "app-company-offers",
  templateUrl: "./company-offers.component.html",
  styleUrls: [ "./company-offers.component.scss" ],
})
export class CompanyOffersComponent implements OnInit {
  readonly logoSrc = "assets/logos/logo_wetalk.png";
  readonly appointmentUrl = "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2-ZhJT1SZLgHTsmxekuyfATPGBXGi_B1PjvIqdlNnANdu4yBDiYL_1UCcM6ci_yR2cgayECEsh";

  readonly cards: OfferPricingCard[] = [
    {
      planCode: "wetalk",
      tierLabel: "",
      priceLabel: "",
      metricValue: "600€",
      metricSuffix: "/ parcours",
      metricNote: "Sans abonnement annuel. Chaque parcours est facturé à l’usage.",
      bullets: [
        "Bilan émotionnel personnalisé",
        "Appels d’orientation offerts",
        "Ressources liées aux parcours actifs",
      ],
      boldBulletCount: 0,
      ctaLabel: "Prendre rendez-vous",
    },
    {
      planCode: "wetalk-plus",
      tierLabel: "Plus",
      priceLabel: "À partir de",
      metricValue: "10€",
      metricSuffix: "/ an / collaborateur",
      metricNote: "Abonnement plateforme annuel. Les parcours restent facturés séparément.",
      bullets: [
        "Bilan émotionnel personnalisé",
        "Parcours à 10 crédits selon besoin",
        "Baromètre d’équipe",
        "Accès à + de 300 contenus sur notre plateforme (podcasts, contenus vidéos, etc.)",
        "Dashboard avec analyse d’impact",
        "Pilotage personnalisé",
        "-10% sur les fresques, ateliers et conférences",
      ],
      boldBulletCount: 0,
      promoLabel: "Parcours en supplément",
      promoOriginalPrice: "600€/parcours",
      promoPrice: "450€/parcours",
      promoBadge: "-25%",
      ctaLabel: "Prendre rendez-vous",
    },
    {
      planCode: "wetalk-coach",
      tierLabel: "Coach",
      priceLabel: "À partir de",
      metricValue: "10€",
      metricSuffix: "/ mois / collaborateur",
      metricNote: "Soit 120€/an/collaborateur. Paiement annuel.",
      bullets: [
        "Tout ce qui est inclus dans We talk Plus",
        "10 crédits inclus / collaborateur",
        "Dashboard avec analyse d’impact",
        "Pilotage personnalisé",
        "-20% sur les fresques, ateliers et conférences",
      ],
      boldBulletCount: 0,
      includedLabel: "Accès inclus",
      includedValue: "1 parcours / personne inclus (Parole ou Coach)",
      promoLabel: "Parcours supplémentaires We talk Parole & Coach",
      promoOriginalPrice: "600€/parcours",
      promoPrice: "390€/parcours",
      promoBadge: "-35%",
      ctaLabel: "Obtenir l'offre",
      featured: true,
    },
  ];

  currentPlanCode: OfferPlanCode | "legacy" = "legacy";
  currentPlanLabel?: string;
  canLaunchManagedOfferWizard = false;

  private managedOrganizations: { id: string; name: string }[] = [];
  private selectedOrganizationId?: string;

  constructor(
    private readonly profileService: ProfileService,
    private readonly router: Router,
    private readonly dialog: DialogService,
    private readonly storageService: StorageService,
    private readonly organizationStoreService: OrganizationStoreService,
    private readonly organizationRepository: OrganizationRepository,
    private readonly managedOfferCheckoutService: ManagedOfferCheckoutService,
  ) {}

  ngOnInit(): void {
    this.loadOfferContext();
  }

  requestProposal(planCode?: OfferPlanCode): void {
    this.router.navigate([ "/questions" ], {
      queryParams: {
        source: "company-offers-2026",
        subject: "subscription_plan",
        plan: planCode,
      },
    });
  }

  openAppointment(): void {
    window.open(this.appointmentUrl, "_blank", "noopener,noreferrer");
  }

  handleCardAction(planCode: OfferPlanCode): void {
    if (this.isCurrentPlan(planCode)) {
      return;
    }

    if (!this.canLaunchManagedOfferWizard) {
      this.openAppointment();
      return;
    }

    this.dialog.open(OfferActivationWizardComponent, {
      preselectedPlanCode: planCode,
      organizations: this.managedOrganizations,
      selectedOrganizationId: this.selectedOrganizationId,
    }).pipe(take(1))
      .subscribe((result) => {
        if (!result?.activatedOrganizationId) {
          return;
        }

        this.profileService.loadProfile()
          .pipe(
            take(1),
            catchError(() => of(undefined)),
          )
          .subscribe(() => this.loadOfferContext());
      });
  }

  getCardCtaLabel(planCode: OfferPlanCode): string {
    if (this.isCurrentPlan(planCode)) {
      return "Offre actuelle";
    }

    if (this.canLaunchManagedOfferWizard) {
      return `Obtenir ${this.managedOfferCheckoutService.getPlanDisplayName(planCode)}`;
    }

    return "Prendre rendez-vous";
  }

  isCurrentPlan(planCode: OfferPlanCode): boolean {
    return planCode === this.currentPlanCode;
  }

  private loadOfferContext(): void {
    this.profileService
      .getMyProfile()
      .pipe(
        take(1),
        catchError(() => of(undefined)),
        switchMap((profile) =>
          this.resolvePreferredOrganization$(profile).pipe(
            take(1),
            catchError(() => of(undefined)),
            map((organization) => ({ profile, organization })),
          )),
      )
      .subscribe(({ profile, organization }) => {
        const snapshot = getOfferContextSnapshot(organization);

        this.currentPlanCode = snapshot.currentPlanCode;
        this.currentPlanLabel = snapshot.currentPlanLabel;
        this.canLaunchManagedOfferWizard =
          (profile?.role === Role.Talker || profile?.role === Role.Admin)
          && this.managedOrganizations.length > 0;
      });
  }

  private resolvePreferredOrganization$(profile?: Profile): Observable<Organization | undefined> {
    if (profile?.role === Role.Talker) {
      return of(this.resolveTalkerPreferredOrganization(profile));
    }

    if (profile?.role === Role.Admin) {
      return this.resolveAdminPreferredOrganization();
    }

    this.resetManagedOrganizations();
    return of(undefined);
  }

  private resolveTalkerPreferredOrganization(talkerProfile: Talker): Organization | undefined {
    const organizations = (talkerProfile.organizationAssociations ?? [])
      .map((association) => association.organization)
      .filter((organization): organization is NonNullable<typeof organization> => !!organization)
      .reduce((acc, organization) => {
        if (acc.some((item) => item.id === organization.id)) {
          return acc;
        }

        acc.push(organization);
        return acc;
      }, [] as NonNullable<Talker["organizationAssociations"][number]["organization"]>[]);

    this.managedOrganizations = organizations.map((organization) => ({
      id: organization.id,
      name: organization.name,
    }));

    const storedOrganizationId = this.storageService.getOrganizationSelectedId();
    const preferredOrganizationId = organizations.some((organization) => organization.id === storedOrganizationId)
      ? storedOrganizationId
      : organizations.find((organization) => organization.id === talkerProfile.organization?.id)?.id
        ?? organizations[0]?.id;

    this.selectedOrganizationId = preferredOrganizationId;

    return organizations.find((organization) => organization.id === preferredOrganizationId)
      ?? talkerProfile.organization;
  }

  private resolveAdminPreferredOrganization(): Observable<Organization | undefined> {
    return this.organizationStoreService.getObservable()
      .pipe(
        take(1),
        switchMap((organizationFromStore) => {
          if (organizationFromStore?.id) {
            this.setManagedOrganizations([ organizationFromStore ], organizationFromStore.id);
            return of(organizationFromStore);
          }

          const storedOrganizationId = this.storageService.getOrganizationSelectedId();
          if (!storedOrganizationId) {
            this.resetManagedOrganizations();
            return of(undefined);
          }

          return this.organizationRepository.findById(storedOrganizationId)
            .pipe(
              take(1),
              map((organization) => {
                this.setManagedOrganizations([ organization ], organization.id);
                return organization;
              }),
              catchError(() => {
                this.resetManagedOrganizations();
                return of(undefined);
              }),
            );
        }),
      );
  }

  private setManagedOrganizations(organizations: Organization[], selectedOrganizationId?: string): void {
    this.managedOrganizations = organizations.map((organization) => ({
      id: organization.id,
      name: organization.name,
    }));
    this.selectedOrganizationId = selectedOrganizationId ?? organizations[0]?.id;
  }

  private resetManagedOrganizations(): void {
    this.managedOrganizations = [];
    this.selectedOrganizationId = undefined;
  }
}
